/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { metaMcpAddMember } from "../funcs/metaMcpAddMember.js";
import { metaMcpCreate } from "../funcs/metaMcpCreate.js";
import { metaMcpDelete } from "../funcs/metaMcpDelete.js";
import { metaMcpGet } from "../funcs/metaMcpGet.js";
import { metaMcpList } from "../funcs/metaMcpList.js";
import { metaMcpListMembers } from "../funcs/metaMcpListMembers.js";
import { metaMcpRemoveMember } from "../funcs/metaMcpRemoveMember.js";
import { metaMcpUpdate } from "../funcs/metaMcpUpdate.js";
import { metaMcpUpdateMember } from "../funcs/metaMcpUpdateMember.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { ListMetaMcpMembersResult } from "../models/components/listmetamcpmembersresult.js";
import { ListMetaMcpServersResult } from "../models/components/listmetamcpserversresult.js";
import { MetaMcpMember } from "../models/components/metamcpmember.js";
import { MetaMcpServer } from "../models/components/metamcpserver.js";
import {
  AddMetaMcpMemberRequest,
  AddMetaMcpMemberSecurity,
} from "../models/operations/addmetamcpmember.js";
import {
  CreateMetaMcpServerRequest,
  CreateMetaMcpServerSecurity,
} from "../models/operations/createmetamcpserver.js";
import {
  DeleteMetaMcpServerRequest,
  DeleteMetaMcpServerSecurity,
} from "../models/operations/deletemetamcpserver.js";
import {
  GetMetaMcpServerRequest,
  GetMetaMcpServerSecurity,
} from "../models/operations/getmetamcpserver.js";
import {
  ListMetaMcpMembersRequest,
  ListMetaMcpMembersSecurity,
} from "../models/operations/listmetamcpmembers.js";
import {
  ListMetaMcpServersRequest,
  ListMetaMcpServersSecurity,
} from "../models/operations/listmetamcpservers.js";
import {
  RemoveMetaMcpMemberRequest,
  RemoveMetaMcpMemberSecurity,
} from "../models/operations/removemetamcpmember.js";
import {
  UpdateMetaMcpMemberRequest,
  UpdateMetaMcpMemberSecurity,
} from "../models/operations/updatemetamcpmember.js";
import {
  UpdateMetaMcpServerRequest,
  UpdateMetaMcpServerSecurity,
} from "../models/operations/updatemetamcpserver.js";
import { unwrapAsync } from "../types/fp.js";

export class MetaMcp extends ClientSDK {
  /**
   * addMetaMcpMember metaMcp
   *
   * @remarks
   * Add an MCP server to a meta MCP server's member set
   */
  async addMember(
    request: AddMetaMcpMemberRequest,
    security?: AddMetaMcpMemberSecurity | undefined,
    options?: RequestOptions,
  ): Promise<MetaMcpMember> {
    return unwrapAsync(metaMcpAddMember(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createMetaMcpServer metaMcp
   *
   * @remarks
   * Create a new meta MCP server
   */
  async create(
    request: CreateMetaMcpServerRequest,
    security?: CreateMetaMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<MetaMcpServer> {
    return unwrapAsync(metaMcpCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteMetaMcpServer metaMcp
   *
   * @remarks
   * Delete a meta MCP server. Its live memberships and MCP endpoints are deleted along with it.
   */
  async delete(
    request: DeleteMetaMcpServerRequest,
    security?: DeleteMetaMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(metaMcpDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMetaMcpServer metaMcp
   *
   * @remarks
   * Get a meta MCP server by id
   */
  async get(
    request: GetMetaMcpServerRequest,
    security?: GetMetaMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<MetaMcpServer> {
    return unwrapAsync(metaMcpGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMetaMcpServers metaMcp
   *
   * @remarks
   * List meta MCP servers for a project
   */
  async list(
    request?: ListMetaMcpServersRequest | undefined,
    security?: ListMetaMcpServersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListMetaMcpServersResult> {
    return unwrapAsync(metaMcpList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listMetaMcpMembers metaMcp
   *
   * @remarks
   * List the members of a meta MCP server, ordered by sort order
   */
  async listMembers(
    request: ListMetaMcpMembersRequest,
    security?: ListMetaMcpMembersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListMetaMcpMembersResult> {
    return unwrapAsync(metaMcpListMembers(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * removeMetaMcpMember metaMcp
   *
   * @remarks
   * Remove a member from a meta MCP server
   */
  async removeMember(
    request: RemoveMetaMcpMemberRequest,
    security?: RemoveMetaMcpMemberSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(metaMcpRemoveMember(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateMetaMcpServer metaMcp
   *
   * @remarks
   * Update a meta MCP server. Omitting user_session_issuer_id preserves the stored issuer, and a gateway that would end up without one gets a dedicated issuer minted — an update can never leave a gateway serving anonymously. Omitting visibility or network_access_mode preserves the respective stored value, so a caller that does not manage those policies cannot change them by saving an unrelated field.
   */
  async update(
    request: UpdateMetaMcpServerRequest,
    security?: UpdateMetaMcpServerSecurity | undefined,
    options?: RequestOptions,
  ): Promise<MetaMcpServer> {
    return unwrapAsync(metaMcpUpdate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateMetaMcpMember metaMcp
   *
   * @remarks
   * Update a meta MCP membership's sort order
   */
  async updateMember(
    request: UpdateMetaMcpMemberRequest,
    security?: UpdateMetaMcpMemberSecurity | undefined,
    options?: RequestOptions,
  ): Promise<MetaMcpMember> {
    return unwrapAsync(metaMcpUpdateMember(
      this,
      request,
      security,
      options,
    ));
  }
}
